import './css/ReviewAndOptimize.css';
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { expandEnergyChargeData } from "./EnergyRate";
import { LoadingSpinner } from "../../general/LoadingSpinner";
import { FaExclamationTriangle } from 'react-icons/fa';

const ReviewAndOptimize = ({
  selectedRoutes,
  selectedDepots,
  selectedChargingStations,
  selectedTrucks,
  routes,
  customUserTrucks,
  selectedCustomUserTrucks,
  depots,
  chargingStations,
  trucks,
  energyChargeData,
  otherParameters,
  prevStep,
  setLoadingOverlay
}) => {
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");
  const [showCaseNameModal, setShowCaseNameModal] = useState(false);
  const [caseName, setCaseName] = useState("");
  const [loading, setLoading] = useState(false);
  const [optimizationWarnings, setOptimizationWarnings] = useState([]);

  console.log("otherParameters", otherParameters);

  // Handle loading overlay class
  useEffect(() => {
    if (loading) {
      document.body.classList.add('loading-active');
    } else {
      document.body.classList.remove('loading-active');
    }

    return () => {
      document.body.classList.remove('loading-active');
    };
  }, [loading]);

  // Handle optimization
  const handleOptimize = async (e) => {
    e.preventDefault();
    setLoading(true);

    const expandedEnergyCharges = expandEnergyChargeData(energyChargeData);

    try {
      const response = await axios.post(
        API_ENDPOINTS.optimize,
        {
          case_name: caseName,
          selected_routes: selectedRoutes,
          selected_depots: selectedDepots,
          selected_charging_stations: selectedChargingStations,
          selected_trucks: [...selectedTrucks, ...selectedCustomUserTrucks],
          tou_rates: expandedEnergyCharges,
          other_parameters: otherParameters,
        },
        {
          headers: {
            Authorization: `Token ${authToken}`,
          },
        }
      );
      navigate("/analytics");
    } catch (error) {
      console.error("Optimization failed:", error);
      setLoading(false);
      // You should add an error notification here for the user
    }
  };

  // Open case name modal
  const openCaseNameModal = () => {
    // Generate warnings
    const warnings = [];
    
    if (selectedDepots.length === 0) {
      warnings.push("No depots selected. Only public charging stations will be available.");
    }
    
    if (selectedChargingStations.length === 0 && selectedDepots.length === 0) {
      warnings.push("No charging locations selected. Optimization may not find feasible solutions.");
    }
    
    setOptimizationWarnings(warnings);
    setShowCaseNameModal(true);
  };

  // Handle case name submit
  const handleCaseNameSubmit = (e) => {
    e.preventDefault();
    setShowCaseNameModal(false);
    handleOptimize(e);
  };

  // Suggested case names
  const suggestedCaseNames = [
    "Typical Day 1",
    "Busy Day",
    "Weekend Operations",
    "Holiday Schedule",
    "Peak Season"
  ];
  
  // Apply suggested case name
  const applySuggestion = (suggestion) => {
    setCaseName(suggestion);
  };

  // Get selected items for summary
  const selectedTruckItems = trucks.filter(truck =>
    selectedTrucks.includes(truck.truck_id)
  ).map(truck => ({
    ...truck,
  }));

  const selectedRouteItems = routes.filter(route =>
    selectedRoutes.includes(route.id)
  );

  const selectedDepotItems = depots.filter(depot =>
    selectedDepots.includes(depot.depot_id)
  );

  const selectedChargingStationItems = chargingStations.filter(station =>
    selectedChargingStations.includes(station.cs_id)
  );

  const SummaryCard = ({ title, count, children }) => (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-bold text-gray-800 mb-3">{title} ({count})</h3>
      <ul className="space-y-3 max-h-60 overflow-y-auto pr-2">{children}</ul>
    </div>
  );

  const SummaryListItem = ({ primary, secondary }) => (
    <li className="text-sm text-gray-700 flex justify-between items-center">
      <span>{primary}</span>
      <span className="text-gray-500 text-right">{secondary}</span>
    </li>
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <SummaryCard title="Selected Routes" count={selectedRoutes.length}>
          {routes.filter(r => selectedRoutes.includes(r.id)).map(r => (
            <SummaryListItem key={r.id} primary={r.raw_route_name} secondary={`${(r.distances.reduce((a, b) => a + b, 0) / 1609.34).toFixed(1)} mi`} />
          ))}
        </SummaryCard>

        <SummaryCard title="Selected Depots" count={selectedDepots.length}>
          {depots.filter(d => selectedDepots.includes(d.depot_id)).map(d => (
            <SummaryListItem key={d.depot_id} primary={d.name || `Depot ${d.depot_id}`} secondary={d.address} />
          ))}
        </SummaryCard>

        <SummaryCard title="Selected Charging Stations" count={selectedChargingStations.length}>
          {chargingStations.filter(s => selectedChargingStations.includes(s.cs_id)).map(s => (
            <SummaryListItem key={s.cs_id} primary={s.name || `Station ${s.cs_id}`} secondary={`${s.max_power} kW`} />
          ))}
        </SummaryCard>

        <SummaryCard title="Selected Vehicles" count={selectedTrucks.length + selectedCustomUserTrucks.length}>
          {trucks.filter(t => selectedTrucks.includes(t.truck_id)).map(t => (
            <SummaryListItem key={t.truck_id} primary={`${t.make} ${t.model}`} secondary={`$${t.price.toLocaleString()}`} />
          ))}
          {customUserTrucks.filter(t => selectedCustomUserTrucks.includes(t.id)).map(t => (
            <SummaryListItem key={t.id} primary={`${t.make} ${t.model}`} secondary={`$${t.price.toLocaleString()}`} />
          ))}
        </SummaryCard>

        <SummaryCard title="Time-of-Use Rates" count={3}>
          <SummaryListItem primary="Peak rate" secondary={`$${Math.max(...energyChargeData.map(d => d.y)).toFixed(4)}/kWh`} />
          <SummaryListItem primary="Off-peak rate" secondary={`$${Math.min(...energyChargeData.map(d => d.y)).toFixed(4)}/kWh`} />
          <SummaryListItem primary="Average rate" secondary={`$${(energyChargeData.reduce((s, d) => s + d.y, 0) / 24).toFixed(4)}/kWh`} />
        </SummaryCard>

        <SummaryCard title="Other Parameters" count={3}>
          <SummaryListItem primary="Diesel maintenance" secondary={`$${otherParameters.diesel_maint}/mile`} />
          <SummaryListItem primary="Electric maintenance" secondary={`$${otherParameters.elec_maint}/mile`} />
          {/* <SummaryListItem primary="Non-ZEV penalty" secondary={`$${otherParameters.non_ze_penalty_price}/day`} /> */}
        </SummaryCard>
      </div>

      <div className="mt-8 text-center">
        <button
          onClick={openCaseNameModal}
          disabled={selectedTrucks.length === 0 || selectedRoutes.length === 0}
          className="px-8 py-3 bg-blue-600 text-white font-bold text-lg rounded-lg shadow-md hover:bg-blue-700 transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Run Optimization
        </button>
      </div>

      {showCaseNameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Name Your Case</h3>
            <p className="text-gray-600 mb-4">Give this optimization case a descriptive name:</p>

            {optimizationWarnings.length > 0 && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0"><FaExclamationTriangle className="h-5 w-5 text-yellow-400" /></div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      {optimizationWarnings.map((warning, i) => <span key={i} className="block">{warning}</span>)}
                      Do you want to continue anyway?
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleCaseNameSubmit}>
              <input type="text" value={caseName} onChange={(e) => setCaseName(e.target.value)} placeholder="e.g., Summer Peak Season" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-1">Suggestions:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedCaseNames.map(name => (
                    <button key={name} type="button" onClick={() => applySuggestion(name)} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200">{name}</button>
                  ))}
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setShowCaseNameModal(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">Cancel</button>
                <button type="submit" disabled={!caseName.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400">Confirm & Run</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && <LoadingSpinner message="Running optimization. This may take a few moments..." />}
    </div>
  );
};

export default ReviewAndOptimize; 